import Vector4 from './Vector4';
import Vector3 from "mediamonks-webgl/renderer/math/Vector3";

export default class Color extends Vector4 {
  get r(): number {
    return this.x;
  }

  get g(): number {
    return this.y;
  }

  get b(): number {
    return this.z;
  }

  get a(): number {
    return this.w;
  }

  set r(value: number) {
    this.x = value;
  }

  set g(value: number) {
    this.y = value;
  }

  set b(value: number) {
    this.z = value;
  }

  set a(value: number) {
    this.w = value;
  }

  public toRGBString(): string {
    return 'rgb(' + Math.round(this.r * 255) + ',' + Math.round(this.g * 255) + ',' + Math.round(this.b * 255) + ')';
  }

  // does not always return rounded numbers (sometimes a number is x.999999 and the hex value is invalid
  public static componentToHex(c: number): string {
    const hex = ((c * 255) | 0).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }

  public getHex(): string {
    return '#' + Color.componentToHex(this.r) + Color.componentToHex(this.g) + Color.componentToHex(this.b);
  }

  public setRGBA255(value: number[]): Color {
    this.r = value[0] / 255;
    this.g = value[1] / 255;
    this.b = value[2] / 255;
    this.a = value[3];
    return this;
  }

  public getRGBA255(): number[] {
    return [Math.round(this.r * 255), Math.round(this.g * 255), Math.round(this.b * 255), this.a];
  }

  public setFromHex(hex: string): Color {
    hex = hex.replace(/[^0-9A-F]/gi, '');
    const bigint: number = parseInt(hex, 16);
    this.r = ((bigint >> 16) & 255) / 255;
    this.g = ((bigint >> 8) & 255) / 255;
    this.b = (bigint & 255) / 255;
    this.a = 1;
    return this;
  }

  public static fromHex(hex: string): Color {
    return new Color().setFromHex(hex);
  }

  public static fromRGB255(value: number[]): Color {
    const rgba: Color = new Color();
    rgba.setRGBA255(value);
    return rgba;
  }

  public static getRandom(): Color {
    return new Color(Math.random(), Math.random(), Math.random());
  }

  public static colorFromString(input: string): Color {
    const values: number[] = [];
    const tokens: string[] = [];
    tokens[0] = input.substr(0, Math.floor(input.length / 3));
    tokens[1] = input.substr(Math.floor(input.length / 3), Math.floor(2 * input.length / 3));
    tokens[2] = input.substr(Math.floor(2 * input.length / 3), input.length);

    for (let j = 0; j < tokens.length; j++) {
      for (let i = 0; i < tokens[j].length; i++) {
        if (!(j in values)) {
          values[j] = 0;
        }
        values[j] += tokens[j].charCodeAt(i);
      }
    }

    const color = new Color((values[0] / 128) % 1, (values[1] / 128) % 1, (values[2] / 128) % 1, 1);
    return color;
  }

  public static fromHSV(h: number, s: number, v: number): Color {
    return new Color().setFromHSV(h, s, v);
  }

  // provide normalized hsv
  public setFromHSV(h: number, s: number, v: number): Color {
    let r = 0;
    let g = 0;
    let b = 0;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
      case 0 :
        r = v, g = t, b = p;
        break;
      case 1 :
        r = q, g = v, b = p;
        break;
      case 2 :
        r = p, g = v, b = t;
        break;
      case 3 :
        r = p, g = q, b = v;
        break;
      case 4 :
        r = t, g = p, b = v;
        break;
      case 5 :
        r = v, g = p, b = q;
        break;
    }
    this.r = r;
    this.g = g;
    this.b = b;
    return this;
  }

  public getRgbaString(): string {
    return 'rgba(' + Math.round(this.r * 255) + ', ' + Math.round(this.g * 255) + ', ' + Math.round(this.b * 255) + ', ' + this.a + ')';
  }

  public toVector3(): Vector3 {
    return new Vector3(this.x, this.y, this.z);
  }
}